export const getAuthErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    // Sign up errors
    case "auth/email-already-in-use":
      return "This email is already registered. Please try signing in instead.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Password should be at least 6 characters long.";

    // Sign in errors
    case "auth/user-not-found":
      return "No account found with this email. Please sign up instead.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support.";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect email or password. If you haven't signed in recently, try resetting your password.";

    // Email verification errors
    case "auth/verification-required":
      return "Email verification required. Please enter the code sent to your email.";
    case "auth/invalid-verification-token":
      return "Invalid verification. Please request a new code.";
    case "auth/verification-expired":
      return "Verification code expired. Please request a new code.";
    case "auth/invalid-verification-code":
      return "Invalid verification code. Please try again.";

    // Social provider account errors
    case "auth/account-uses-google-signin":
      return 'This email is linked to Google sign-in. Please use "Continue with Google" to log in.';
    case "auth/account-uses-apple-signin":
      return 'This email is linked to Apple sign-in. Please use "Continue with Apple" to log in.';
    case "auth/account-uses-microsoft-signin":
      return 'This email is linked to Microsoft sign-in. Please use "Continue with Microsoft" to log in.';

    // Account linking errors
    case "auth/account-exists-with-different-credential":
      return "An account already exists with this email using a different sign-in method.";
    case "auth/credential-already-in-use":
      return "This credential is already linked to a different account.";
    case "auth/provider-already-linked":
      return "This sign-in method is already linked to your account.";

    // Popup errors
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "";
    case "auth/popup-blocked":
      return "The sign-in popup was blocked by your browser. Please allow popups for this site or try another sign-in method.";

    case "auth/unknown-error":
    default:
      return "An unexpected error occurred. Please try again.";
  }
};

/**
 * Resolve a user-facing message from a caught error. Prefers a known auth/
 * code mapping; otherwise includes the raw error message so native-plugin
 * failures (e.g. Capgo Google sign-in on Android) surface useful detail
 * instead of the generic fallback.
 */
export const getSignInErrorMessage = (err: unknown): string => {
  const errObj =
    err != null && typeof err === "object"
      ? (err as { code?: unknown; message?: unknown })
      : undefined;
  const rawCode = typeof errObj?.code === "string" ? errObj.code : undefined;
  const rawMessage =
    typeof errObj?.message === "string" ? errObj.message : undefined;
  // Only treat `auth/*` codes as recognised — getAuthErrorMessage's switch
  // hard-codes those and returns the generic fallback for everything else,
  // which would otherwise clobber useful native-plugin messages like
  // "Google Sign-In failed: [28444] Developer console is not set up correctly"
  // that come with codes like "SIGN_IN_FAILED".
  if (
    rawCode &&
    rawCode.startsWith("auth/") &&
    rawCode !== "auth/unknown-error"
  ) {
    return getAuthErrorMessage(rawCode);
  }
  if (rawMessage) return `Sign-in failed: ${rawMessage}`;
  return getAuthErrorMessage("auth/unknown-error");
};

/**
 * Report a caught sign-in error to the console and, if the mapped message
 * is non-empty, feed it to the caller's setError. Cancelled-popup codes
 * map to an empty string and are intentionally skipped so they don't clear
 * a prior meaningful error.
 */
export const applySignInError = (
  err: unknown,
  setError: (message: string) => void,
): void => {
  console.error("Authentication error:", err);
  const message = getSignInErrorMessage(err);
  if (message) setError(message);
};
